import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { Box, Flex, Heading, Icon, Input, useModalState } from '@stoplight/mosaic';
import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useFilterEndpoints } from '../../hooks/useFilterEndpoints';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { Endpoint, LogoProps, SearchResult } from '../../types';
import { Logo } from '../Logo';
import { Search } from '../Search/Search';
import type { CustomLinkComponent, TableOfContentsItem } from '../TableOfContents';
import { TableOfContents } from '../TableOfContents';
import { MobileTopNav } from './MobileTopNav';

type ResponsiveSidebarLayoutProps = {
  maxContentWidth?: number;
  sidebarWidth?: number;
  children?: React.ReactNode;
  name: string;
  logo?: string | LogoProps;
  tree?: TableOfContentsItem[];
  allEndpoints: Endpoint[];
  onTocClick?(): void;
};

const MAX_CONTENT_WIDTH = 1800;
const SIDEBAR_MIN_WIDTH = 300;
const SIDEBAR_MAX_WIDTH = 1.5 * SIDEBAR_MIN_WIDTH;

export const ResponsiveSidebarLayout = React.forwardRef<HTMLDivElement, ResponsiveSidebarLayoutProps>(
  (
    {
      children,
      name,
      logo,
      tree,
      allEndpoints,
      onTocClick,
      maxContentWidth = MAX_CONTENT_WIDTH,
      sidebarWidth = SIDEBAR_MIN_WIDTH,
    },
    ref,
  ) => {
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const [sidebarRef, currentSidebarWidth, startResizing] = useResizer(sidebarWidth);
    const { pathname } = useLocation();
    const [search, setSearch] = React.useState('');
    const navigate = useNavigate();
    const { isOpen, open, close } = useModalState();
    const { data, isFetching } = useFilterEndpoints({
      search,
      allEndpoints,
    });

    React.useEffect(() => {
      // Scroll to top on page change
      scrollRef.current?.scrollTo(0, 0);
    }, [pathname]);

    const handleClose = () => {
      close();
      setSearch('');
    };

    const handleClick = (searchResult: SearchResult) => {
      navigate(searchResult.uri);
      handleClose();
    };

    const { isResponsiveLayoutEnabled } = useResponsiveLayout();

    return (
      <Flex ref={ref} className="sl-elements-api" pin h="full">
        {!isResponsiveLayoutEnabled && (
          <Flex
            ref={sidebarRef}
            onMouseDown={(e: React.MouseEvent<HTMLElement>) => e.preventDefault()}
            style={{ maxWidth: `${SIDEBAR_MAX_WIDTH}px` }}
          >
            <Flex
              direction="col"
              bg="canvas-100"
              borderR
              pt={8}
              pos="sticky"
              pinY
              style={{
                paddingLeft: `calc((100% - ${maxContentWidth}px) / 2)`,
                width: `${currentSidebarWidth}px`,
                minWidth: `${SIDEBAR_MIN_WIDTH}px`,
              }}
            >
              <Box
                display="inline-block"
                pb={8}
                pr={8}
                pl={8}
                rounded="lg"
                overflowY="hidden"
                overflowX="hidden"
                style={{ backgroundColor: 'transparent' }}
              >
                <img
                  src="https://www.efoy-cloud.com/imgs/logo.svg"
                  style={{ maxWidth: '236px', height: '73px' }}
                  alt="logo"
                />
              </Box>
              <Box pb={6}>
                <Input
                  border
                  data-test="docs-search-input"
                  display="inline-block"
                  appearance="minimal"
                  icon={<Box as={Icon} ml={1} icon={faSearch} />}
                  placeholder="Search..."
                  type="search"
                  w="full"
                  size="lg"
                  onClick={open}
                />
                <Search
                  isLoading={isFetching}
                  search={search}
                  searchResults={data}
                  onSearch={setSearch}
                  isOpen={isOpen}
                  onClose={handleClose}
                  onClick={handleClick}
                />
              </Box>
              <Box flex={1} overflowY="auto">
                <Sidebar
                  name={name}
                  logo={logo}
                  tree={tree!}
                  pathname={pathname}
                  onTocClick={onTocClick}
                  isInResponsiveMode={false}
                />
              </Box>
            </Flex>
            <Flex
              justifySelf="end"
              flexGrow={0}
              flexShrink={0}
              resize="x"
              onMouseDown={startResizing}
              style={{ width: '1em', flexBasis: '6px', cursor: 'ew-resize' }}
            />
          </Flex>
        )}

        <Box ref={scrollRef} bg="canvas" px={!isResponsiveLayoutEnabled ? 24 : 6} flex={1} w="full" overflowY="auto">
          <Box style={{ maxWidth: `${maxContentWidth - currentSidebarWidth}px` }} py={16}>
            {children}
          </Box>
        </Box>

        {isResponsiveLayoutEnabled && (
          <MobileTopNav onTocClick={onTocClick!} name={name} logo={logo} tree={tree!} pathname={pathname} />
        )}
      </Flex>
    );
  },
);

type SidebarRef = React.Ref<HTMLDivElement>;
type SidebarWidth = number;
type StartResizingFn = () => void;

export const Sidebar = ({
  name,
  logo,
  tree,
  pathname,
  onTocClick,
  isInResponsiveMode,
}: {
  name: string;
  logo?: string | LogoProps;
  tree: TableOfContentsItem[];
  pathname: string;
  onTocClick?(): void;
  isInResponsiveMode: boolean;
}) => {
  return (
    <>
      <Flex ml={4} mb={5} alignItems="center">
        {typeof logo === 'string' ? (
          <Logo logo={{ url: logo, altText: 'logo' }} />
        ) : (
          typeof logo === 'object' && <Logo logo={logo} />
        )}
        <Heading size={4}>{name}</Heading>
      </Flex>
      <Flex flexGrow flexShrink overflowY="auto" direction="col">
        <TableOfContents
          tree={tree}
          activeId={pathname}
          Link={Link as CustomLinkComponent}
          onLinkClick={onTocClick}
          isInResponsiveMode={isInResponsiveMode}
        />
      </Flex>
    </>
  );
};

function useResizer(sidebarWidth: number): [SidebarRef, SidebarWidth, StartResizingFn] {
  const sidebarRef = React.useRef<HTMLDivElement | null>(null);
  const [isResizing, setIsResizing] = React.useState(false);
  const [currentSidebarWidth, setCurrentSidebarWidth] = React.useState(sidebarWidth);

  const startResizing = React.useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = React.useCallback(
    mouseMoveEvent => {
      if (isResizing) {
        const value = mouseMoveEvent.clientX - sidebarRef.current!.getBoundingClientRect().left;
        setCurrentSidebarWidth(Math.min(Math.max(SIDEBAR_MIN_WIDTH, value), SIDEBAR_MAX_WIDTH));
      }
    },
    [isResizing],
  );

  React.useEffect(() => {
    window?.addEventListener('mousemove', resize);
    window?.addEventListener('mouseup', stopResizing, { passive: true });
    return () => {
      window?.removeEventListener('mousemove', resize);
      window?.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  return [sidebarRef, currentSidebarWidth, startResizing];
}
